/**
 * Where focus goes when a drill closes, and what the set says out loud.
 *
 * Both belong to the SET rather than to any item, and both are keyed by the
 * item's own key. The implementation this replaces ran a
 * `document.querySelectorAll` over a generated instance id inside a layout
 * effect: the behaviour was right and the mechanism could not survive a
 * re-keyed list, because the node it went looking for was identified by where
 * it sat rather than by what it was.
 *
 * One polite live region per set, never one per pillar. Eighteen families each
 * announcing their own state changes is a screen reader talking over itself.
 */
"use client";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { CRC_VISUALLY_HIDDEN } from "./cresultcard.tokens";

/** The item's one control. Focus lands on it, not on the quiet click zone. */
const TITLE_SELECTOR = "[data-crc-title]";

export interface CresultcardFocusRegistry {
  /** Registers an item's surface and returns its own unregister. */
  readonly register: (key: string, node: HTMLElement | null) => () => void;
  /** Returns focus to the item that was open. */
  readonly focus: (key: string) => void;
}

export interface CresultcardAnnouncer {
  readonly announce: (message: string) => void;
}

interface CresultcardSetServices
  extends CresultcardFocusRegistry,
    CresultcardAnnouncer {}

const NO_OP_SERVICES: CresultcardSetServices = {
  register: () => () => undefined,
  focus: () => undefined,
  announce: () => undefined,
};

const CresultcardSetServicesContext =
  createContext<CresultcardSetServices | null>(null);

interface CresultcardSetServicesProviderProps {
  readonly children: ReactNode;
}

export function CresultcardSetServicesProvider({
  children,
}: CresultcardSetServicesProviderProps): ReactElement {
  const nodesRef = useRef<Map<string, HTMLElement>>(new Map());
  const [message, setMessage] = useState("");

  const register = useCallback(
    (key: string, node: HTMLElement | null): (() => void) => {
      const nodes = nodesRef.current;
      if (node == null) {
        nodes.delete(key);
        return () => undefined;
      }
      nodes.set(key, node);
      // An outgoing surface and its replacement share a key while the drill
      // animates. The one that leaves must not delete the entry the one that
      // arrived has already written, or focus restoration finds nothing.
      return () => {
        if (nodes.get(key) === node) {
          nodes.delete(key);
        }
      };
    },
    [],
  );

  const focus = useCallback((key: string): void => {
    const node = nodesRef.current.get(key);
    if (node == null) {
      return;
    }
    const title = node.querySelector<HTMLElement>(TITLE_SELECTOR);
    (title ?? node).focus();
  }, []);

  const announce = useCallback((next: string): void => {
    setMessage(next);
  }, []);

  const services = useMemo<CresultcardSetServices>(
    (): CresultcardSetServices => ({ announce, focus, register }),
    [announce, focus, register],
  );

  return (
    <CresultcardSetServicesContext.Provider value={services}>
      {children}
      <span
        aria-live="polite"
        data-el="Cresultcard/SetServicesProvider/Status"
        style={CRC_VISUALLY_HIDDEN}
      >
        {message}
      </span>
    </CresultcardSetServicesContext.Provider>
  );
}

function useCresultcardSetServices(): CresultcardSetServices {
  return useContext(CresultcardSetServicesContext) ?? NO_OP_SERVICES;
}

/** An item registers itself here; the set focuses by key when a drill closes. */
export function useCresultcardFocusRegistry(): CresultcardFocusRegistry {
  return useCresultcardSetServices();
}

/** The set's own voice. Disclosure state is NOT announced — `aria-expanded` carries it. */
export function useCresultcardAnnouncer(): CresultcardAnnouncer {
  return useCresultcardSetServices();
}
